import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import styled from 'styled-components'
import axios from 'axios'
import { authAPI } from '../../api'
import { useTurnstile } from '../../hooks/useTurnstile'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { useAuth } from '../../context/AuthContext'
import type { User } from '../../types'
import StyledAlert from '../../components/StyledAlert'
import { StyledPasswordInput } from '../../components/StyledPasswordInput'
import { AppModal } from '../../components/AppModal'
import {
  FormGroup,
  FormLabel,
  FormInput,
  FieldError,
  PrimaryButton,
  GrayButton,
  colors,
  transitions
} from '../../theme/sharedComponents'

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required.').email('Please enter a valid email.'),
  password: z.string().min(1, 'Password is required.'),
})

const twoFASchema = z.object({
  code: z.string().length(6, 'Verification code must be 6 digits.').regex(/^\d{6}$/, 'Verification code must be numeric.'),
})

type LoginFormData = z.infer<typeof loginSchema>
type TwoFAFormData = z.infer<typeof twoFASchema>

const ForgotPasswordLink = styled(Link)`
  color: ${colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  transition: ${transitions.default};
  font-weight: 500;

  &:hover {
    color: ${colors.primaryLight};
    text-decoration: underline;
  }
`

const LoginModal: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()

  // Modal visibility state
  const show = searchParams.get('login') === 'true' && !isAuthenticated
  const message = searchParams.get('message') || undefined

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false)
  const [twoFAEmail, setTwoFAEmail] = useState('')
  const [apiError, setApiError] = useState('')

  // Rate limiting state
  const [rateLimitedUntil, setRateLimitedUntil, removeRateLimit] = useLocalStorage<number | null>('login_rate_limit_until', null)
  const [countdown, setCountdown] = useState<number>(0)

  // Turnstile hook
  const { turnstileRef, getToken, reset: resetTurnstile } = useTurnstile(show)

  // Form setup
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const twoFAForm = useForm<TwoFAFormData>({
    resolver: zodResolver(twoFASchema),
    defaultValues: { code: '' },
  })

  // Countdown timer for rate limiting
  useEffect(() => {
    if (!rateLimitedUntil) {
      setCountdown(0)
      return
    }

    const updateCountdown = () => {
      const now = Date.now()
      const remaining = Math.ceil((rateLimitedUntil - now) / 1000)

      if (remaining <= 0) {
        setCountdown(0)
        removeRateLimit()
        setApiError('')
      } else {
        setCountdown(remaining)
        if (!apiError) {
          setApiError(`Too many login attempts. Please try again in ${remaining} seconds.`)
        }
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [rateLimitedUntil, apiError, removeRateLimit])

  const handleLoginSubmit = async (data: LoginFormData) => {
    setApiError('')

    const turnstileToken = getToken()
    if (!turnstileToken) {
      setApiError('Please complete the security verification.')
      return
    }

    try {
      const response = await authAPI.login(data.email, data.password, turnstileToken)

      if (response.requires_2fa) {
        setRequires2FA(true)
        setTwoFAEmail(response.email || data.email)
        loginForm.setValue('password', '')
        setApiError('')
        resetTurnstile()
        return
      }

      login(response.user as User, response.sessionExpiresAt)
      handleClose()

      if (response.user.is_admin) {
        navigate('/admin-portal')
      } else {
        navigate('/customer-portal')
      }
    } catch (error: unknown) {
      console.error('Login error:', error)

      if (axios.isAxiosError(error) && error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after']
        const waitSeconds = retryAfter ? parseInt(retryAfter, 10) : 60
        const limitUntil = Date.now() + waitSeconds * 1000
        setRateLimitedUntil(limitUntil)
        setApiError(`Too many login attempts. Please try again in ${waitSeconds} seconds.`)
      } else if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.message || 'Login failed. Please check your credentials and try again.')
      } else {
        setApiError('Login failed. Please check your connection and try again.')
      }

      resetTurnstile()
    }
  }

  const handleVerify2FA = async (data: TwoFAFormData) => {
    setApiError('')

    try {
      const response = await authAPI.verify2FA(twoFAEmail, data.code)

      login(response.user as User, response.sessionExpiresAt)
      handleClose()
      navigate('/admin-portal')
    } catch (error: unknown) {
      console.error('2FA verification error:', error)
      twoFAForm.reset()

      if (axios.isAxiosError(error) && error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after']
        const waitSeconds = retryAfter ? parseInt(retryAfter, 10) : 300
        const limitUntil = Date.now() + waitSeconds * 1000
        setRateLimitedUntil(limitUntil)
        setApiError(`Too many verification attempts. Please try again in ${waitSeconds} seconds.`)
      } else if (axios.isAxiosError(error)) {
        setApiError(error.response?.data?.message || 'Invalid or expired verification code. Please try again.')
      } else {
        setApiError('Verification failed. Please try again.')
      }
    }
  }

  const handleClose = () => {
    resetTurnstile()
    searchParams.delete('login')
    searchParams.delete('message')
    setSearchParams(searchParams)
    loginForm.reset()
    twoFAForm.reset()
    setApiError('')
    setRequires2FA(false)
    setTwoFAEmail('')
  }

  const handleShow = () => {
    searchParams.set('login', 'true')
    setSearchParams(searchParams)
  }

  const loginIsSubmitting = loginForm.formState.isSubmitting
  const twoFAIsSubmitting = twoFAForm.formState.isSubmitting
  // Compute error message for AppModal error prop
  const errorMessage = apiError ? (countdown > 0
    ? `Too many ${requires2FA ? 'verification' : 'login'} attempts. Please try again in ${countdown} seconds.`
    : apiError
  ) : null

  return (
    <>
      <button
        id="login-modal-trigger"
        onClick={handleShow}
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      <AppModal show={show} onHide={handleClose} title="Login" icon="bi bi-shield-lock" error={errorMessage}>
        <>
          {message === 'password-changed' && !requires2FA && (
            <StyledAlert variant="success" className="mb-3">
              <strong>Password Changed Successfully!</strong>
              <div>Please log in with your new password.</div>
            </StyledAlert>
          )}

          {!requires2FA ? (
            // Step 1: Email + Password Login Form
            <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)}>
              <FormGroup>
                <FormLabel>Email</FormLabel>
                <FormInput
                  type="email"
                  placeholder="Enter your email"
                  maxLength={120}
                  {...loginForm.register('email')}
                />
                {loginForm.formState.errors.email && (
                  <FieldError>{loginForm.formState.errors.email.message}</FieldError>
                )}
              </FormGroup>

              <FormGroup>
                <FormLabel>Password</FormLabel>
                <StyledPasswordInput
                  placeholder="Enter your password"
                  {...loginForm.register('password')}
                />
                {loginForm.formState.errors.password && (
                  <FieldError>{loginForm.formState.errors.password.message}</FieldError>
                )}
              </FormGroup>

              <div className="text-end mb-3">
                <ForgotPasswordLink
                  to="/forgot-password"
                  onClick={() => {
                    handleClose()
                  }}
                >
                  Forgot Password?
                </ForgotPasswordLink>
              </div>

              {/* Turnstile CAPTCHA */}
              <div className="mb-3 d-flex justify-content-center">
                <div ref={turnstileRef}></div>
              </div>

              <div className="d-grid">
                <PrimaryButton
                  type="submit"
                  className="sheen-hover"
                  disabled={loginIsSubmitting || countdown > 0}
                >
                  {loginIsSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Logging in...
                    </>
                  ) : countdown > 0 ? (
                    <>
                      <i className="bi bi-hourglass-split me-2"></i>
                      Try again in {countdown}s
                    </>
                  ) : (
                    <>
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Login
                    </>
                  )}
                </PrimaryButton>
              </div>
            </form>
          ) : (
            // Step 2: 2FA Verification Form
            <form onSubmit={twoFAForm.handleSubmit(handleVerify2FA)}>
              <StyledAlert variant="info" className="mb-3">
                <strong><i className="bi bi-envelope-check me-2"></i>Verification Required</strong>
                <div className="mt-2">
                  A 6-digit verification code has been sent to <strong>{twoFAEmail}</strong>
                </div>
                <div className="mt-1" style={{ fontSize: '0.9rem' }}>
                  The code expires in 5 minutes.
                </div>
              </StyledAlert>

              <FormGroup>
                <FormLabel>Verification Code</FormLabel>
                <FormInput
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  autoFocus
                  style={{
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                  {...twoFAForm.register('code', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '')
                    }
                  })}
                />
                {twoFAForm.formState.errors.code && (
                  <FieldError>{twoFAForm.formState.errors.code.message}</FieldError>
                )}
                <small style={{ color: colors.textLightModeMuted, fontSize: '0.9rem', display: 'block', marginTop: '0.35rem' }}>
                  Enter the 6-digit code from your email
                </small>
              </FormGroup>

              <div className="d-grid gap-2">
                <PrimaryButton
                  type="submit"
                  className="sheen-hover"
                  disabled={twoFAIsSubmitting || countdown > 0}
                >
                  {twoFAIsSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Verifying...
                    </>
                  ) : countdown > 0 ? (
                    <>
                      <i className="bi bi-hourglass-split me-2"></i>
                      Try again in {countdown}s
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check me-2"></i>
                      Verify Code
                    </>
                  )}
                </PrimaryButton>
                <GrayButton
                  type="button"
                  onClick={handleClose}
                  disabled={twoFAIsSubmitting}
                  className="sheen-hover"
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Cancel &amp; Start Over
                </GrayButton>
              </div>
            </form>
          )}
        </>
      </AppModal>
    </>
  )
}

export default LoginModal
